(() => {
class EventsPage extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.yearLinks = [];
    this.yearSections = [];
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.yearLinks = Array.from(this.querySelectorAll('[data-year-link]'));
    this.yearSections = Array.from(this.querySelectorAll('[data-year-section]'));

    if (this.yearLinks.length === 0 || this.yearSections.length === 0) return;

    for (const link of this.yearLinks) {
      link.addEventListener('click', this.handleYearLinkClick, {
        signal: this.abortController.signal,
      });
    }

    window.addEventListener('hashchange', this.handleHashChange, {
      signal: this.abortController.signal,
    });

    const hashYear = this.getYearFromHash();
    const initialYear = hashYear || this.dataset.initialYear || this.yearLinks[0].dataset.yearLink;
    this.updateCurrentYear(initialYear);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  handleYearLinkClick = (event) => {
    const link = event.currentTarget;
    const target = this.getLinkTarget(link);

    // 链接目标异常时保留浏览器原生行为，避免脚本阻断页面导航。
    if (!target) return;

    event.preventDefault();
    const year = link.dataset.yearLink;
    this.updateCurrentYear(year);
    this.updateLocationHash(target.id);
    target.scrollIntoView({
      behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
      block: 'start',
    });
    target.focus({ preventScroll: true });
  };

  handleHashChange = () => {
    const year = this.getYearFromHash();
    if (year) this.updateCurrentYear(year);
  };

  getLinkTarget(link) {
    const href = link.getAttribute('href');
    if (!href?.startsWith('#')) return null;

    const target = document.getElementById(href.slice(1));
    if (!target || !this.contains(target)) return null;
    return target;
  }

  getYearFromHash() {
    const targetId = window.location.hash.slice(1);
    if (!targetId) return '';

    const matchingSection = this.yearSections.find((section) => section.id === targetId);
    return matchingSection?.dataset.yearSection || '';
  }

  updateCurrentYear(year) {
    for (const link of this.yearLinks) {
      const isCurrent = link.dataset.yearLink === year;
      if (isCurrent) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  updateLocationHash(targetId) {
    const nextUrl = new URL(window.location.href);
    nextUrl.hash = targetId;
    window.history.pushState({}, '', nextUrl);
  }
}

if (!customElements.get('events-page')) {
  customElements.define('events-page', EventsPage);
}

class EventsNewsletter extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.dialog = null;
    this.openButton = null;
    this.closeButton = null;
    this.form = null;
    this.nameInput = null;
    this.tagsInput = null;
    this.returnFocusElement = null;
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.dialog = this.querySelector('[data-events-newsletter-dialog]');
    this.openButton = this.querySelector('[data-events-newsletter-open]');
    this.closeButton = this.querySelector('[data-events-newsletter-close]');
    this.form = this.querySelector('.events-page__newsletter-form');
    this.nameInput = this.querySelector('[data-events-newsletter-name]');
    this.tagsInput = this.querySelector('[data-events-newsletter-tags]');

    // 缺少任一核心节点时保留静态内容，避免局部配置异常影响整页脚本。
    if (!this.dialog || !this.openButton || !this.closeButton) return;

    const listenerOptions = { signal: this.abortController.signal };
    this.openButton.addEventListener('click', this.handleOpenClick, listenerOptions);
    this.closeButton.addEventListener('click', this.handleCloseClick, listenerOptions);
    this.dialog.addEventListener('click', this.handleBackdropClick, listenerOptions);
    this.dialog.addEventListener('keydown', this.handleDialogKeydown, listenerOptions);

    if (this.form && this.nameInput && this.tagsInput) {
      this.nameInput.addEventListener('input', this.updateCustomerTags, listenerOptions);
      this.form.addEventListener('submit', this.updateCustomerTags, listenerOptions);
      this.updateCustomerTags();
    }

    // Shopify 服务端返回成功或错误时，自动重新打开弹窗展示反馈。
    if (this.dialog.hasAttribute('data-events-newsletter-auto-open')) {
      this.openDialog({ showStatusFirst: true });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    document.body.classList.remove('events-page-newsletter-open');
  }

  handleOpenClick = (event) => {
    this.returnFocusElement = event.currentTarget;
    this.openDialog({ showStatusFirst: false });
  };

  handleCloseClick = () => {
    this.closeDialog();
  };

  handleBackdropClick = (event) => {
    if (event.target === this.dialog) this.closeDialog();
  };

  handleDialogKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const movingBeforeFirst = event.shiftKey && document.activeElement === firstElement;
    const movingAfterLast = !event.shiftKey && document.activeElement === lastElement;

    // 普通浮层需要手动限制焦点，避免键盘用户进入被蒙层遮挡的页面内容。
    if (movingBeforeFirst || movingAfterLast) {
      event.preventDefault();
      (movingBeforeFirst ? lastElement : firstElement).focus();
    }
  };

  handleDialogClose = () => {
    const focusTarget = this.returnFocusElement || this.openButton;
    if (focusTarget?.isConnected) focusTarget.focus();
    this.returnFocusElement = null;
  };

  updateCustomerTags = () => {
    if (!this.nameInput || !this.tagsInput) return;

    const baseTags = this.tagsInput.dataset.baseTags || 'newsletter, Events Newsletter';
    const subscriberName = this.nameInput.value
      .trim()
      .replace(/[,;]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);

    // Customer 表单未承诺写入姓名字段，因此同步为标签，确保后台始终可追溯姓名。
    this.tagsInput.value = subscriberName ? `${baseTags}, Subscriber: ${subscriberName}` : baseTags;
  };

  getFocusableElements() {
    if (!this.dialog) return [];

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(this.dialog.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true',
    );
  }

  openDialog({ showStatusFirst }) {
    if (!this.dialog || !this.dialog.hidden) return;

    // 不使用原生 dialog top layer，确保 Shopify 注入到 body 的 hCaptcha challenge 可见。
    this.dialog.hidden = false;
    this.dialog.setAttribute('aria-hidden', 'false');
    document.body.classList.add('events-page-newsletter-open');

    const status = this.dialog.querySelector('[data-events-newsletter-status]');
    const initialField = this.dialog.querySelector('[data-events-newsletter-initial-focus]');
    const focusTarget = showStatusFirst && status ? status : initialField || this.closeButton;
    window.requestAnimationFrame(() => focusTarget?.focus());
  }

  closeDialog() {
    if (!this.dialog || this.dialog.hidden) return;

    this.dialog.hidden = true;
    this.dialog.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('events-page-newsletter-open');
    this.handleDialogClose();
  }
}

if (!customElements.get('events-newsletter')) {
  customElements.define('events-newsletter', EventsNewsletter);
}
})();
